export const sqlLessons = [
  {
    slug: 'select',
    title: 'SELECT',
    icon: '🔍',
    content: `
# SQL SELECT

The \`SELECT\` statement is the foundation of SQL. It retrieves data from one or more tables.

## Basic Syntax

\`\`\`sql
SELECT column1, column2
FROM table_name;
\`\`\`

## Select All Columns

\`\`\`sql
SELECT * FROM employees;
\`\`\`

## Select Specific Columns

\`\`\`sql
SELECT first_name, last_name, salary
FROM employees;
\`\`\`

## Using Aliases

\`\`\`sql
SELECT
  first_name AS "First Name",
  salary * 12 AS annual_salary
FROM employees;
\`\`\`

## DISTINCT — Remove Duplicates

\`\`\`sql
SELECT DISTINCT department
FROM employees;
\`\`\`

## ORDER BY — Sort Results

\`\`\`sql
SELECT first_name, salary
FROM employees
ORDER BY salary DESC;
\`\`\`

## LIMIT / TOP — Restrict Rows

\`\`\`sql
-- MySQL / PostgreSQL
SELECT * FROM employees LIMIT 10;

-- SQL Server
SELECT TOP 10 * FROM employees;
\`\`\`

## Key Takeaways

- Use \`SELECT *\` only during development; specify columns in production
- \`DISTINCT\` removes duplicate rows from the result set
- \`ORDER BY\` defaults to ascending (ASC); use DESC for descending
- Always pair \`LIMIT\` with \`ORDER BY\` for deterministic results
`
  },
  {
    slug: 'where',
    title: 'WHERE',
    icon: '🔎',
    content: `
# SQL WHERE

The \`WHERE\` clause filters rows based on conditions, returning only matching records.

## Basic Syntax

\`\`\`sql
SELECT column1, column2
FROM table_name
WHERE condition;
\`\`\`

## Comparison Operators

\`\`\`sql
-- Equal
SELECT * FROM employees WHERE department = 'Engineering';

-- Greater / Less than
SELECT * FROM employees WHERE salary > 50000;
SELECT * FROM employees WHERE age < 30;

-- Not equal
SELECT * FROM employees WHERE status != 'inactive';
\`\`\`

## Logical Operators: AND / OR / NOT

\`\`\`sql
-- AND: both conditions must be true
SELECT * FROM employees
WHERE department = 'Engineering' AND salary > 70000;

-- OR: at least one condition must be true
SELECT * FROM employees
WHERE department = 'Sales' OR department = 'Marketing';

-- NOT: negate a condition
SELECT * FROM employees
WHERE NOT department = 'HR';
\`\`\`

## IN — Match Multiple Values

\`\`\`sql
SELECT * FROM employees
WHERE department IN ('Engineering', 'Design', 'Product');
\`\`\`

## BETWEEN — Range Filter

\`\`\`sql
SELECT * FROM employees
WHERE salary BETWEEN 40000 AND 80000;
\`\`\`

## LIKE — Pattern Matching

\`\`\`sql
-- Starts with 'J'
SELECT * FROM employees WHERE first_name LIKE 'J%';

-- Contains 'son'
SELECT * FROM employees WHERE last_name LIKE '%son%';

-- Exactly 5 characters
SELECT * FROM employees WHERE code LIKE '_____';
\`\`\`

## NULL Checks

\`\`\`sql
-- Rows where email is missing
SELECT * FROM employees WHERE email IS NULL;

-- Rows where email exists
SELECT * FROM employees WHERE email IS NOT NULL;
\`\`\`

## Key Takeaways

- Never use \`= NULL\` — always use \`IS NULL\`
- \`LIKE\` is case-insensitive in most databases
- Wrap \`OR\` groups in parentheses to avoid logic bugs
- \`IN\` is cleaner than multiple \`OR\` conditions
`
  },
  {
    slug: 'joins',
    title: 'JOINS',
    icon: '🔗',
    content: `
# SQL JOINS

JOINs combine rows from two or more tables based on a related column.

## Types of JOINs

| JOIN Type | Returns |
|-----------|---------|
| INNER JOIN | Only matching rows in both tables |
| LEFT JOIN | All rows from left + matching from right |
| RIGHT JOIN | All rows from right + matching from left |
| FULL JOIN | All rows from both tables |
| CROSS JOIN | Every combination of rows |

## INNER JOIN

Returns only rows where there is a match in **both** tables.

\`\`\`sql
SELECT
  employees.name,
  departments.department_name
FROM employees
INNER JOIN departments
  ON employees.department_id = departments.id;
\`\`\`

## LEFT JOIN

Returns all rows from the left table, even if there is no match on the right.

\`\`\`sql
SELECT
  employees.name,
  departments.department_name
FROM employees
LEFT JOIN departments
  ON employees.department_id = departments.id;
-- Employees with no department will have NULL for department_name
\`\`\`

## RIGHT JOIN

Returns all rows from the right table, even if there is no match on the left.

\`\`\`sql
SELECT
  employees.name,
  departments.department_name
FROM employees
RIGHT JOIN departments
  ON employees.department_id = departments.id;
-- Departments with no employees will still appear
\`\`\`

## Multiple JOINs

\`\`\`sql
SELECT
  e.name,
  d.department_name,
  p.project_name
FROM employees e
INNER JOIN departments d ON e.department_id = d.id
LEFT JOIN project_assignments pa ON e.id = pa.employee_id
LEFT JOIN projects p ON pa.project_id = p.id;
\`\`\`

## Self JOIN

Join a table to itself — useful for hierarchical data.

\`\`\`sql
SELECT
  e.name AS employee,
  m.name AS manager
FROM employees e
LEFT JOIN employees m ON e.manager_id = m.id;
\`\`\`

## Key Takeaways

- Always use table aliases (\`e\`, \`d\`) to keep queries readable
- \`INNER JOIN\` is the most common — use it when both sides must exist
- \`LEFT JOIN\` is safer when child records may not exist
- NULL in a JOIN result means no matching row was found
`
  },
  {
    slug: 'indexes',
    title: 'INDEXES',
    icon: '⚡',
    content: `
# SQL INDEXES

Indexes dramatically speed up data retrieval at the cost of write performance and storage.

## What is an Index?

An index is a separate data structure (typically a B-Tree) that maps column values to row locations — like a book index pointing to page numbers.

**Without index:** Full table scan → O(n)

**With index:** B-Tree lookup → O(log n)

## Creating an Index

\`\`\`sql
-- Basic single-column index
CREATE INDEX idx_employees_email
ON employees(email);

-- Unique index (prevents duplicate values)
CREATE UNIQUE INDEX idx_employees_email_unique
ON employees(email);

-- Composite index (multiple columns)
CREATE INDEX idx_employees_dept_salary
ON employees(department_id, salary);
\`\`\`

## Types of Indexes

### Primary Key Index (automatic)

\`\`\`sql
CREATE TABLE employees (
  id INT PRIMARY KEY,  -- index created automatically
  name VARCHAR(100)
);
\`\`\`

### Unique Index

\`\`\`sql
CREATE UNIQUE INDEX idx_unique_email ON employees(email);
\`\`\`

### Composite Index

\`\`\`sql
-- Optimizes: WHERE department_id = 5 AND salary > 50000
CREATE INDEX idx_dept_sal ON employees(department_id, salary);
\`\`\`

### Partial Index (PostgreSQL)

\`\`\`sql
-- Index only active employees
CREATE INDEX idx_active_employees
ON employees(email)
WHERE status = 'active';
\`\`\`

## When to Use Indexes

**Index these columns:**
- Columns in \`WHERE\` clauses
- Columns in \`JOIN\` conditions
- Columns in \`ORDER BY\`
- Foreign key columns

**Skip indexes on:**
- Columns with very few distinct values (e.g., boolean)
- Tables with very few rows
- Columns that are rarely queried

## Checking Index Usage

\`\`\`sql
EXPLAIN SELECT * FROM employees WHERE email = 'test@example.com';
\`\`\`

## Dropping an Index

\`\`\`sql
DROP INDEX idx_employees_email;
\`\`\`

## Key Takeaways

- Indexes speed reads but slow writes (INSERT/UPDATE/DELETE must update indexes)
- The **leftmost columns** of a composite index must appear in the query
- Use \`EXPLAIN\` to verify your index is actually being used
- Too many indexes hurt write performance — index strategically
`
  }
];
